// Simple logger with levels for client-side use
const logger = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  
  // Default level - set to WARN by default to disable debug logs unless explicitly enabled
  level: 2, // WARN level by default
  
  // Control whether auth errors are shown or suppressed
  // Set to true to show auth errors, false to suppress
  showAuthErrors: true,

  // Global debug flag - can be toggled at runtime
  // Only set to true if explicitly enabled in .env with REACT_APP_DEBUG_ENABLED=true
  // Initialize with a safe check for the environment variable
  debugEnabled: false,
  
  // Component-specific debug flags - safely access env vars
  debugComponents: [],
  
  setLevel(level) {
    this.level = level;
  },

  // Enable or disable debugging globally
  setDebugEnabled(enabled) {
    this.debugEnabled = enabled;
    // If debugging is enabled, set level to DEBUG
    if (enabled) {
      this.level = this.DEBUG;
    } else {
      // If debugging is disabled, set level back to WARN
      this.level = this.WARN;
    }
    // Store in localStorage for persistence across page refreshes
    try {
      localStorage.setItem('debug_enabled', enabled.toString());
    } catch (e) {
      // Ignore localStorage errors
    }
  },

  // Set which components should have debug logging enabled
  setDebugComponents(components = []) {
    this.debugComponents = Array.isArray(components) ? components : [components];
    // Store in localStorage for persistence
    try {
      localStorage.setItem('debug_components', this.debugComponents.join(','));
    } catch (e) {
      // Ignore localStorage errors
    }
  },

  // Check if debug is enabled for a component
  isDebugEnabled(component = null) {
    if (!this.debugEnabled) return false;
    if (!component) return true;
    return this.debugComponents.length === 0 || this.debugComponents.includes(component);
  },
  
  debug(message, ...args) {
    // Only log debug messages if the debug flag is explicitly enabled
    if (this.debugEnabled && this.level <= this.DEBUG) {
      console.log(`DEBUG: ${message}`, ...args);
    }
  },

  // Component-specific debug logging
  debugComponent(component, message, ...args) {
    if (this.isDebugEnabled(component) && this.level <= this.DEBUG) {
      console.log(`DEBUG [${component}]: ${message}`, ...args);
    }
  },
  
  info(message, ...args) {
    if (this.level <= this.INFO) {
      console.info(`INFO: ${message}`, ...args);
    }
  },
  
  warn(message, ...args) {
    if (this.level <= this.WARN) {
      console.warn(`WARN: ${message}`, ...args);
    }
  },
  
  error(message, ...args) {
    // Skip certain auth-related errors when not needed
    if (!this.showAuthErrors && 
        (message.includes('Authentication required') || 
         message.includes('No authenticated user'))) {
      return;
    }
    
    if (this.level <= this.ERROR) {
      console.error(`ERROR: ${message}`, ...args);
    }
  }
};

// Function to safely get environment variables
const getEnvVar = (name) => {
  if (typeof process !== 'undefined' && process.env && process.env[name] !== undefined) {
    return process.env[name];
  }
  
  if (typeof window !== 'undefined' && 
      window.process && 
      window.process.env && 
      window.process.env[name] !== undefined) {
    return window.process.env[name];
  }
  
  return undefined;
};

// Initialize from environment variables
try {
  // Check if debugging is enabled in environment
  const envDebugEnabled = getEnvVar('REACT_APP_DEBUG_ENABLED') === 'true';
  logger.debugEnabled = envDebugEnabled;
  
  // Set level based on debug setting
  logger.level = envDebugEnabled ? logger.DEBUG : logger.WARN;
  
  // Get component-specific debug settings
  const envDebugComponents = getEnvVar('REACT_APP_DEBUG_COMPONENTS');
  if (envDebugComponents) {
    logger.debugComponents = envDebugComponents.split(',').filter(Boolean);
  }
  
  // Only use localStorage if environment enables debugging
  if (envDebugEnabled && typeof localStorage !== 'undefined') {
    const storedDebugEnabled = localStorage.getItem('debug_enabled');
    if (storedDebugEnabled !== null) {
      logger.debugEnabled = storedDebugEnabled === 'true';
    }
    
    const storedDebugComponents = localStorage.getItem('debug_components');
    if (storedDebugComponents) {
      logger.debugComponents = storedDebugComponents.split(',').filter(Boolean);
    }
  }
} catch (e) {
  // If there's any error, ensure debugging is off
  logger.debugEnabled = false;
  logger.level = logger.WARN;
  console.error('Error initializing logger:', e);
}

export default logger;